// @ts-check

// See tests/shared/bem-test.js for usage

/**
 * @typedef {string} BlockName
 * Encapsulates a standalone entity that is meaningful on its own.
 * While blocks can be nested and interact with each other,
 * semantically they remain equal; there is no precedence or hierarchy.
 *
 * Block names may consist of Latin letters, digits, and dashes.
 * @see http://getbem.com/naming/
 */

/**
 * @typedef {string} ElementName
 * Parts of a block and have no standalone meaning.
 * Any element is semantically tied to its block.
 *
 * Element names may consist of Latin letters, digits, dashes and underscores.
 * @see http://getbem.com/naming/
 */

/**
 * @typedef {Object.<string, (string | number | boolean | void)>} ModifiersObject
 *
 * input: `{ key1: 'val', key2: 2, key3: true, key4: '', key5: false, key6: null }`
 * parsed: `[ 'key1-val', 'key2-2', 'key3', 'key4' ]`
 */

/**
 * @typedef {Array.<string | number | false | void>} ModifiersArray
 *
 * input: `[ 'mod1', 2, false, null ]`
 * parsed: `[ 'mod1', 2 ]`
 */

/**
 * @typedef {string | ModifiersArray | ModifiersObject} Modifiers
 */

/**
 * @typedef {'className' | 'selector'} JoinClassesMode
 */

/**
 * @typedef {string} JoinedClassName
 * Space-separated classes with no dots
 *
 * ex: `block block--mod-1 block--mod-2`
 */

/**
 * @typedef {string} JoinedSelector
 * Dot-separated classes with a leading dot
 *
 * ex: `.block.block--mod-1.block--mod-2`
 */

/**
 * @typedef {JoinedClassName | JoinedSelector} JoinedClasses
 * Depending on `JoinClassesMode`, either a:
 *   - `className` (Space-separated with no dots)
 *   - `selector` (Dot-separated with a leading dot)
 */

/**
 *
 * @param {BlockName} blockName
 * @param {JoinClassesMode} [mode]
 * @returns {BemEntity}
 */
function Bem(blockName, mode) {
  return Bem.entity(blockName, mode);
}

// joinClasses mode types
/** @type {JoinClassesMode} */
Bem.CLASSNAME_MODE = 'className'; // Space-separated
/** @type {JoinClassesMode} */
Bem.SELECTOR_MODE = 'selector'; // Dot-separated with leading dot

// Bem token separators: http://getbem.com/naming/
Bem.ELEMENT_TOKEN_SEPARATOR = '__';
Bem.MODIFIER_TOKEN_SEPARATOR = '--';
Bem.MODIFIER_KEY_VALUE_SEPARATOR = '-';

// Static methods

/**
 * @param {BlockName} blockName
 * @param {JoinClassesMode} [mode]
 * @returns {BemEntity}
 */
Bem.entity = function (blockName, mode) {
  return new BemEntity(blockName, mode || Bem.CLASSNAME_MODE);
};

/**
 * @param {BlockName} blockName
 * @param {Modifiers} modifiers
 * @param {JoinClassesMode} mode
 * @returns {JoinedClasses}
 */
Bem.block = function (blockName, modifiers, mode) {
  return Bem.joinClasses(
    Bem.composeModifiers(blockName, modifiers),
    mode
  );
};

/**
 * @param {BlockName} blockName
 * @param {ElementName} elementName
 * @param {Modifiers} modifiers
 * @param {JoinClassesMode} mode
 * @returns {JoinedClasses}
 */
Bem.element = function (blockName, elementName, modifiers, mode) {
  return Bem.joinClasses(
    Bem.composeModifiers(
      Bem.composeElement(blockName, elementName),
      modifiers
    ),
    mode
  );
};

/**
 * @param {BlockName} blockName
 * @param {Modifiers} modifiers
 * @returns {JoinedClassName}
 */
Bem.blockClassName = function (blockName, modifiers) {
  return Bem.block(
    blockName, modifiers,
    Bem.CLASSNAME_MODE
  );
};

/**
 * @param {BlockName} blockName
 * @param {Modifiers} modifiers
 * @returns {JoinedSelector}
 */
Bem.blockSelector = function (blockName, modifiers) {
  return Bem.block(
    blockName, modifiers,
    Bem.SELECTOR_MODE
  );
};

/**
 * @param {BlockName} blockName
 * @param {ElementName} elementName
 * @param {Modifiers} modifiers
 * @returns {JoinedClassName}
 */
Bem.elementClassName = function (blockName, elementName, modifiers) {
  return Bem.element(
    blockName, elementName, modifiers,
    Bem.CLASSNAME_MODE
  );
};

/**
 * @param {BlockName} blockName
 * @param {ElementName} elementName
 * @param {Modifiers} modifiers
 * @returns {JoinedSelector}
 */
Bem.elementSelector = function (blockName, elementName, modifiers) {
  return Bem.element(
    blockName, elementName, modifiers,
    Bem.SELECTOR_MODE
  );
};

// Utility API

/**
 * @param {Array.<string>} classes
 * @param {JoinClassesMode} mode
 * @returns {JoinedClasses}
 */
Bem.joinClasses = function (classes, mode) {
  if (mode === Bem.CLASSNAME_MODE) {
    return classes.join(' ');
  }
  if (mode === Bem.SELECTOR_MODE) {
    if (!classes.length) {
      return '';
    }
    return '.' + classes.join('.');
  }
  throw Error('Unexpected Bem.joinClasses mode: ' + mode);
}

/**
 * @param {BlockName} blockName
 * @param {ElementName} elementName
 * @returns {string}
 */
Bem.composeElement = function (blockName, elementName) {
  return blockName + Bem.ELEMENT_TOKEN_SEPARATOR + elementName;
}

/**
 * @param {string} baseName
 * @param {string | number} modifier
 * @returns {string}
 */
Bem.composeModifier = function (baseName, modifier) {
  return baseName + Bem.MODIFIER_TOKEN_SEPARATOR + modifier;
}

/**
 * @param {string} baseName
 * @param {Modifiers} modifiers
 * @returns {Array.<string>}
 */
Bem.composeModifiers = function (baseName, modifiers) {
  var affirmativeModifiers = Bem.parseModifiers(modifiers);
  if (affirmativeModifiers.length) {
    var modifiersWithBase = affirmativeModifiers.map(function (modifier) {
      return Bem.composeModifier(baseName, modifier);
    });
    return [baseName].concat(modifiersWithBase);
  }
  return [baseName];
}

/**
 * @param {Modifiers} modifiers
 * @returns {Array.<string|number>}
 */
Bem.parseModifiers = function (modifiers) {
  if (!modifiers) {
    return [];
  }
  if (typeof modifiers === 'string') {
    // Convert modifiers string into array
    return Bem.pruneModifiersArray(modifiers.split(' '));
  }
  if (modifiers instanceof Array) {
    return Bem.pruneModifiersArray(modifiers);
  }
  if (modifiers instanceof Object) {
    // Convert modifiers object into array
    return Bem.pruneModifiersArray(
      Bem.squashModifiersObject(modifiers)
    );
  }
}

/**
 * Prune negatory modifiers (except zero)
 * @param {ModifiersArray} modifiers
 * @returns {Array.<string | number>}
 */
Bem.pruneModifiersArray = function (modifiers) {
  // @ts-ignore Only non-empty strings and numbers remain after filtering
  return modifiers.filter(function (mod) {
    return mod === 0 || !!mod;
  });
}

/**
 * @param {ModifiersObject} modifiers
 * @returns {ModifiersArray}
 */
Bem.squashModifiersObject = function (modifiers) {
  // Convert modifiers object into array of 'key'/'key-value' strings
  return Object.keys(modifiers).map(function (key) {
    var value = modifiers[key];
    if (value === false || value == null) {
      return null;
    }
    if (value === true || value === '') {
      return key;
    }
    return key + Bem.MODIFIER_KEY_VALUE_SEPARATOR + value;
  });
}

// Entity Object

class BemEntity {
  /**
   * @param {BlockName} blockName
   * @param {JoinClassesMode} mode
   * @constructor
   */
  constructor(blockName, mode) {
    this.blockName = blockName;
    this.mode = mode;
  }

  /**
   * @param {Modifiers} modifiers
   * @returns {JoinedClasses}
   */
  block(modifiers) {
    return Bem.block(
      this.blockName, modifiers,
      this.mode
    );
  }

  /**
   * @param {ElementName} elementName
   * @param {Modifiers} modifiers
   * @returns {JoinedClasses}
   */
  element(elementName, modifiers) {
    return Bem.element(
      this.blockName, elementName, modifiers,
      this.mode
    );
  }

  /**
   * @param {Modifiers} modifiers
   * @returns {JoinedClassName}
   */
  blockClassName(modifiers) {
    return Bem.blockClassName(this.blockName, modifiers);
  }

  /**
   * @param {ElementName} elementName
   * @param {Modifiers} modifiers
   * @returns {JoinedClassName}
   */
  elementClassName(elementName, modifiers) {
    return Bem.elementClassName(this.blockName, elementName, modifiers);
  }

  /**
   * @param {Modifiers} modifiers
   * @returns {JoinedSelector}
   */
  blockSelector(modifiers) {
    return Bem.blockSelector(this.blockName, modifiers);
  }

  /**
   * @param {ElementName} elementName
   * @param {Modifiers} modifiers
   * @returns {JoinedSelector}
   */
  elementSelector(elementName, modifiers) {
    return Bem.elementSelector(this.blockName, elementName, modifiers);
  }
}

Bem.BemEntity = BemEntity;

module.exports = Bem;
